import React from 'react';
import { Check, Clock, ArrowRight, Plus, Sparkles } from 'lucide-react';
import { services, serviceAddons, TRAVEL_FEE_INFO } from '../utils/mock';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';

// Helper function for badge colors - avoids nested ternaries
const getBadgeColor = (badge) => {
  if (badge === 'PREMIUM') return 'bg-gradient-to-r from-purple-600 to-purple-700';
  if (badge === 'MOST POPULAR') return 'bg-gradient-to-r from-green-600 to-emerald-600';
  if (badge === 'COMING SOON') return 'bg-gradient-to-r from-orange-600 to-orange-700';
  return 'bg-gradient-to-r from-blue-600 to-blue-700';
};

const Services = () => {
  const scrollToBooking = () => {
    const element = document.getElementById('booking');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-12 md:py-16 bg-zinc-950">
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        {/* First-Time Customer Promotion */}
        <div className="max-w-3xl mx-auto mb-6 md:mb-8">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-4 md:p-6 text-center shadow-xl">
            <div className="flex items-center justify-center mb-1 md:mb-2">
              <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-white mr-2" />
              <h3 className="text-xl md:text-2xl font-bold text-white">New Customer Special</h3>
            </div>
            <p className="text-white text-lg md:text-xl font-semibold mb-0.5 md:mb-1">Save $30 on Your First Detail!</p>
            <p className="text-white/90 text-xs md:text-sm">Discount automatically applied • Valid on any service below</p>
          </div>
        </div>
        
        {/* Section Header - More Compact */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-block bg-red-600/20 border border-red-500/50 rounded-full px-3 py-1 md:px-4 md:py-1.5 mb-2 md:mb-3">
            <span className="text-red-500 font-semibold text-xs md:text-sm">Our Services</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-3">
            Premium Detailing Services
          </h2>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            Choose from our professional packages designed to restore and protect your vehicle
          </p>
        </div>

        {/* Services Grid - More Compact */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10">
          {services.map((service) => (
            <Card
              key={service.id}
              className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all duration-300 overflow-hidden group"
            >
              {/* Service Image - Reduced Height */}
              <div className="relative h-32 md:h-40 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent"></div>
                
                {/* Service Badges */}
                {(service.id === 4 || service.id === 5) && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded-full font-bold text-xs shadow-lg">
                    SAVE 15%
                  </div>
                )}
                {service.badge && (
                  <div className={`absolute top-2 right-2 text-white px-3 py-1 rounded-full font-bold text-xs shadow-lg ${
                    getBadgeColor(service.badge)
                  }`}>
                    {service.badge}
                  </div>
                )}
                
                <div className="absolute bottom-2 left-3 right-3">
                  <div className="text-xl font-bold text-white">
                    Starting at ${Math.min(...Object.values(service.pricing))}
                  </div>
                </div>
              </div>

              <CardHeader className="pb-2 md:pb-3 pt-3 md:pt-4">
                <CardTitle className="text-lg md:text-xl text-white">{service.title}</CardTitle>
                <CardDescription className="text-gray-400 text-xs md:text-sm">
                  {service.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="pb-2 md:pb-3">
                {/* Pricing by Vehicle Type - More Compact */}
                <div className="mb-3 md:mb-4 p-2 md:p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <h4 className="text-white font-semibold mb-1.5 md:mb-2 text-xs">Pricing by Vehicle</h4>
                  <div className="grid grid-cols-2 gap-1 md:gap-1.5 text-xs">
                    {Object.entries(service.pricing).map(([vehicle, price]) => (
                      <div key={vehicle} className="flex justify-between text-gray-300">
                        <span>{vehicle}:</span>
                        <span className="font-semibold text-white">${price}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features List - More Compact */}
                <ul className="space-y-1.5 md:space-y-2 mb-2 md:mb-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start space-x-2">
                      <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Duration - More Compact */}
                <div className="flex items-center space-x-2 text-gray-400 mt-2 md:mt-3 pt-2 md:pt-3 border-t border-zinc-800">
                  <Clock className="w-3 h-3 md:w-3.5 md:h-3.5" />
                  <span className="text-xs">{service.duration}</span>
                </div>
              </CardContent>

              <CardFooter className="pt-2 md:pt-3">
                {service.unavailable ? (
                  <div className="w-full bg-zinc-800 text-gray-400 text-center py-2 px-4 rounded-lg border-2 border-zinc-700 cursor-not-allowed text-sm font-semibold">
                    🔒 Coming Soon
                  </div>
                ) : (
                  <Button
                    onClick={scrollToBooking}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold text-sm py-2 group"
                  >
                    Book This Service
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Add-ons Section - More Compact */}
        <div className="mt-12">
          <div className="text-center mb-8">
            <div className="inline-block bg-red-600/20 border border-red-500/50 rounded-full px-4 py-1.5 mb-3">
              <span className="text-red-500 font-semibold text-sm">Service Add-Ons</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Enhance Your Service
            </h3>
            <p className="text-base text-gray-400">
              Add these premium services to any package for the ultimate finish
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {serviceAddons.map((addon) => (
              <Card
                key={addon.id}
                className={`bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all duration-300 ${
                  addon.unavailable ? 'opacity-75' : ''
                }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center">
                        <Plus className="w-5 h-5 text-red-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg text-white">{addon.title}</CardTitle>
                          {addon.unavailable && (
                            <span className="text-xs bg-orange-600 text-white px-2 py-0.5 rounded-full">
                              COMING SOON
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <CardDescription className="text-gray-400 mt-2 text-sm">
                    {addon.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {/* Pricing by Vehicle Type - More Compact */}
                  <div className="mb-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <h4 className="text-white font-semibold mb-2 text-xs">Pricing by Vehicle</h4>
                    <div className="grid grid-cols-2 gap-1.5 text-xs">
                      {Object.entries(addon.pricing).map(([vehicle, price]) => (
                        <div key={vehicle} className="flex justify-between text-gray-300">
                          <span>{vehicle}:</span>
                          <span className="font-semibold text-white">${price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Duration - More Compact */}
                  <div className="flex items-center space-x-2 text-gray-400">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-xs">{addon.duration}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-center space-y-4 mt-16">
          {/* Google Review Prompt - Above Water/Power Notice */}
          <div className="inline-block bg-gradient-to-br from-red-600/10 to-red-700/5 border-2 border-red-500/30 rounded-xl px-8 py-6 mb-6 hover:border-red-500/50 transition-all">
            <div className="flex flex-col items-center space-y-3">
              <div className="flex items-center space-x-2">
                <span className="text-3xl">⭐</span>
                <h3 className="text-white font-bold text-xl">Loved Our Service?</h3>
                <span className="text-3xl">⭐</span>
              </div>
              <p className="text-gray-300 text-base max-w-md">
                Help others discover Mobile Shine! Share your experience on Google and join our community of satisfied customers.
              </p>
              <a
                href="https://g.page/r/CS8IeFV59cyJEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-red-500/50"
              >
                <span className="text-xl">📝</span>
                <span>Leave a Google Review</span>
              </a>
            </div>
          </div>

          {/* Water & Power Requirement Notice */}
          <div className="inline-block bg-blue-900/30 border-2 border-blue-500/50 rounded-lg px-8 py-5 mb-4">
            <div className="flex items-center justify-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-blue-600/20 border border-blue-500/50 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-blue-400 font-bold text-xl">Mobile Service Requirements</h3>
            </div>
            <p className="text-gray-200 text-base leading-relaxed">
              <strong className="text-white">Water & Power Required:</strong> Please ensure access to a standard water spigot and 
              a 15-amp electrical outlet at your location. This allows us to provide the highest quality mobile detailing service 
              right at your doorstep!
            </p>
          </div>
          
          {/* Travel Fee Notice */}
          <div className="inline-block bg-zinc-900 border border-zinc-800 rounded-lg px-6 py-4 mb-4">
            <p className="text-gray-300 text-base">
              <span className="text-green-500 font-semibold">✓ FREE travel</span> within {TRAVEL_FEE_INFO.freeZones.slice(0, 3).join(', ')} & more. 
              <span className="text-gray-400"> Extended areas: ${TRAVEL_FEE_INFO.extendedZones[0].fee}-${TRAVEL_FEE_INFO.extendedZones[TRAVEL_FEE_INFO.extendedZones.length-1].fee} surcharge.</span>
            </p>
          </div>
          
          <p className="text-gray-400 text-lg">
            Need a custom package?{' '}
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-red-500 hover:text-red-400 font-semibold underline"
            >
              Contact us
            </button>{' '}
            for personalized pricing
          </p>
        </div>
      </div>
    </section>
  );
};

export default Services;
